import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  AssignmentTurnedIn,
  CallMade,
  Home,
  LocalHospital,
  Receipt,
  Settings,
  TrendingUp,
  VerifiedUser,
} from "@material-ui/icons";
import type { EpocusRole } from "elephant-web/gql";
import type { ComponentType } from "react";
import {
  linkToEpocusBilling,
  linkToEpocusCredentialing,
  linkToEpocusHome,
  linkToEpocusMe,
  linkToEpocusProgram,
  linkToEpocusQaWorkload,
  linkToEpocusReferralsInbox,
  linkToEpocusReview,
  linkToEpocusSettings,
} from "../links";
import { isAdminNavRole, useEpocusDisplayRole } from "./epocus-role-variant";
import type { Capability } from "./rbac/capabilities";
import { roleCan } from "./rbac/use-can";

/**
 * The capabilities the nav actually gates on — a subset of the full table, kept
 * in lockstep with the grant lookups in `useNavGrants`. Deriving from
 * `Capability` keeps it honest: renaming a capability breaks here too. Gating a
 * nav item on a capability outside this set is a compile error, not a silent
 * runtime hide.
 */
type NavCapability = Extract<
  Capability,
  | "viewReviewQueue"
  | "viewReferrals"
  | "viewProgramDashboard"
  | "viewQaWorkload"
  | "viewCredentialing"
  | "manageBilling"
>;

export type NavBadgeTone = "red" | "amber";

export type NavBadge = {
  countKey: "review" | "referral" | "incomplete";
  tone: NavBadgeTone;
};

export type NavItemDef = {
  /** Full label shown in the desktop sidebar. */
  label: string;
  /** Compact label shown in the mobile bottom nav. */
  shortLabel: string;
  path: () => string;
  icon: ComponentType;
  badge: NavBadge | null;
  /**
   * Capability required to see this item. Omit to make the item visible to
   * every role the containing nav shape is built for.
   */
  capability?: NavCapability;
  /** Feature flag required to see this item, applied on top of any capability. */
  featureFlag?: "referrals";
};

export type NavGroupDef = {
  group: string;
  icon: ComponentType;
  items: NavItemDef[];
};

export type NavEntry = NavItemDef | NavGroupDef;

export const isNavGroup = (entry: NavEntry): entry is NavGroupDef =>
  "group" in entry;

const CLINICIAN_NAV: NavEntry[] = [
  {
    label: "Home",
    shortLabel: "Home",
    path: linkToEpocusHome,
    icon: Home,
    badge: { countKey: "incomplete", tone: "amber" },
  },
  {
    label: "Review Queue",
    shortLabel: "Review",
    path: linkToEpocusReview,
    icon: AssignmentTurnedIn,
    badge: { countKey: "review", tone: "red" },
    capability: "viewReviewQueue",
  },
  {
    label: "Referrals",
    shortLabel: "Referrals",
    path: linkToEpocusReferralsInbox,
    icon: CallMade,
    badge: { countKey: "referral", tone: "red" },
    capability: "viewReferrals",
    featureFlag: "referrals",
  },
  {
    label: "My Performance",
    shortLabel: "Me",
    path: linkToEpocusMe,
    icon: TrendingUp,
    badge: null,
  },
  {
    label: "Settings",
    shortLabel: "Settings",
    path: linkToEpocusSettings,
    icon: Settings,
    badge: null,
  },
];

const ADMIN_NAV: NavEntry[] = [
  {
    label: "Dashboard",
    shortLabel: "Dashboard",
    path: linkToEpocusProgram,
    icon: Home,
    badge: null,
    capability: "viewProgramDashboard",
  },
  {
    label: "QA workload",
    shortLabel: "Workload",
    path: linkToEpocusQaWorkload,
    icon: AssignmentTurnedIn,
    badge: { countKey: "review", tone: "amber" },
    capability: "viewQaWorkload",
  },
  {
    label: "Billing",
    shortLabel: "Billing",
    path: linkToEpocusBilling,
    icon: Receipt,
    badge: null,
    capability: "manageBilling",
  },
  {
    group: "Program",
    icon: LocalHospital,
    items: [
      {
        label: "Credentialing",
        shortLabel: "Credentials",
        path: linkToEpocusCredentialing,
        icon: VerifiedUser,
        badge: null,
        capability: "viewCredentialing",
      },
    ],
  },
  {
    label: "Settings",
    shortLabel: "Settings",
    path: linkToEpocusSettings,
    icon: Settings,
    badge: null,
  },
];

/**
 * `grants` is exhaustive over `NavCapability`, so a newly gated nav item can't
 * compile without a matching lookup here.
 */
const useNavGrants = (
  role: EpocusRole | null,
): ((item: NavItemDef) => boolean) => {
  const grants: Record<NavCapability, boolean> = {
    viewReviewQueue: roleCan(role, "viewReviewQueue"),
    viewReferrals: roleCan(role, "viewReferrals"),
    viewProgramDashboard: roleCan(role, "viewProgramDashboard"),
    viewQaWorkload: roleCan(role, "viewQaWorkload"),
    viewCredentialing: roleCan(role, "viewCredentialing"),
    manageBilling: roleCan(role, "manageBilling"),
  };

  const flagGrants: Record<NonNullable<NavItemDef["featureFlag"]>, boolean> = {
    referrals: useFeatureFlag(featureFlags.epocus.referrals)[0],
  };

  return ({ capability, featureFlag }: NavItemDef) => {
    if (featureFlag && !flagGrants[featureFlag]) return false;
    return capability === undefined || grants[capability];
  };
};

/**
 * Which board a role navigates by is presentation, not authorisation: every
 * item is still gated on the capability table and every route stays guarded
 * independently, so removing an item here never removes access.
 */
export const useVisibleNavEntries = (): NavEntry[] => {
  const role = useEpocusDisplayRole();
  const isVisible = useNavGrants(role);

  const entries = isAdminNavRole(role) ? ADMIN_NAV : CLINICIAN_NAV;

  return entries.flatMap((entry): NavEntry[] => {
    if (!isNavGroup(entry)) return isVisible(entry) ? [entry] : [];
    const items = entry.items.filter(isVisible);
    return items.length > 0 ? [{ ...entry, items }] : [];
  });
};

/**
 * Flattened for the bottom nav: `BottomNavigation` renders one row of actions
 * and drops any child that is not a `BottomNavigationAction`, so a group
 * passed through here disappears rather than nesting.
 */
export const useVisibleNavItems = (): NavItemDef[] =>
  useVisibleNavEntries().flatMap((entry) =>
    isNavGroup(entry) ? entry.items : [entry],
  );
